import * as React from 'react';
import * as ReactDOM from 'react-dom';

export interface ILazyLoad<P, S> extends React.Component<P, S> {
  visible: boolean;
}

export function on(el: HTMLElement | Window, eventName: string, callback: () => void) {
  if (el && el.addEventListener) {
    el.addEventListener(eventName, callback, false);
  }
}

export const throttle = (fn: () => void, threshhold: number = 250) => {
  let last: number;
  let deferTimer: number | NodeJS.Timer;

  return function () {
    const now = Date.now();
    if (last && now < last + threshhold) {
      // hold on to it
      clearTimeout(deferTimer as number);
      deferTimer = setTimeout(function () {
        last = now;
        fn();
      }, threshhold);
    } else {
      last = now;
      fn();
    }
  };
};

export const scrollParent = (node: HTMLElement) => {
  if (!node) {
    return document;
  }

  const excludeStaticParent = node.style.position === 'absolute';
  const overflowRegex = /(scroll|auto)/;
  let parent = node;

  while (parent) {
    if (!parent.parentNode) {
      return node.ownerDocument || document;
    }

    const style = window.getComputedStyle(parent);
    const position = style.position;
    const overflow = style.overflow;
    const overflowX = style.getPropertyValue('overflow-x');
    const overflowY = style.getPropertyValue('overflow-y');

    if (position === 'static' && excludeStaticParent) {
      continue;
    }

    if (overflowRegex.test(overflow + overflowX + overflowY)) {
      return parent;
    }

    parent = parent.parentNode as HTMLElement;
  }

  // tslint:disable-next-line:no-any
  return node.ownerDocument || (node as any).documentElement || document;
};

export const checkOverflowVisible = (component: ILazyLoad<any, any>, parent: HTMLElement) => {
  // eslint-disable-next-line react/no-find-dom-node
  const node = ReactDOM.findDOMNode(component);

  if (!node) {
    return false;
  }

  const { top: parentTop, height: parentHeight } = parent.getBoundingClientRect();
  const windowInnerHeight = window.innerHeight || document.documentElement.clientHeight;

  // calculate top and height of the intersection of the element's scrollParent and viewport
  const intersectionTop = Math.max(parentTop, 0); // intersection's top relative to viewport
  const intersectionHeight = Math.min(windowInnerHeight, parentTop + parentHeight) - intersectionTop; // height

  // check whether the element is visible in the intersection
  const { top, height } = (node as Element).getBoundingClientRect();
  const offsetTop = top - intersectionTop; // element's top relative to intersection

  return (
    offsetTop - (component.props.offset || 0) <= intersectionHeight &&
    offsetTop + height + (component.props.offset || 0) >= 0
  );
};

export const checkNormalVisible = (component: ILazyLoad<any, any>) => {
  // eslint-disable-next-line react/no-find-dom-node
  const node = ReactDOM.findDOMNode(component);

  if (!node) {
    return null;
  }

  const { top, height: elementHeight } = (node as Element).getBoundingClientRect();

  const windowInnerHeight = window.innerHeight || document.documentElement.clientHeight;

  return (
    top - (component.props.offset || 0) <= windowInnerHeight && top + elementHeight + (component.props.offset || 0) >= 0
  );
};

export const checkVisible = (component: ILazyLoad<any, any>) => {
  // eslint-disable-next-line react/no-find-dom-node
  const node = ReactDOM.findDOMNode(component) as HTMLElement;
  if (!node) {
    return false;
  }

  const parent = scrollParent(node);
  const isOverflow = parent !== node.ownerDocument && parent !== document && parent !== document.documentElement;

  const visible = isOverflow ? checkOverflowVisible(component, parent as HTMLElement) : checkNormalVisible(component);

  if (visible) {
    if (!component.visible) {
      component.visible = true;
      component.forceUpdate();

      return true;
    }
  } else if (component.visible) {
    component.visible = false;
  }

  return false;
};
